/**
 * App store — the daily loop's state machine, DOM-free so it can be unit tested.
 *
 * It owns: the device list, the chat list, live session status, the open chat's
 * transcript, the queue, and the composer's draft. It subscribes to the engine
 * through [`ZeronSession`] and never talks to a socket directly.
 *
 * Two rules it enforces, both of which the protocol demands:
 *
 * 1. **Every watch is re-registered through the session**, which reissues them
 *    after a reconnect. Nothing here subscribes directly.
 * 2. **A transcript desync resubscribes.** `TranscriptView.apply` reports false
 *    when a delta did not fit; the store then cancels and reopens the doc watch
 *    for a fresh reset rather than rendering a wrong transcript.
 */

import type { AuthClient } from "../lib/auth";
import { ZeronSession, type SessionState } from "../lib/session";
import {
  type Chat,
  type ChatConfig,
  type Device,
  type EngineInfo,
  type HarnessDescriptor,
  type Model,
  type QueuedMessage,
  type RegistryDevice,
  type SandboxLevel,
  type Session,
  type Space,
  asRegistryDevice,
  type SessionStatus,
  chatIndicator,
  sortChats,
  sortSpaces,
  toMillis
} from "../lib/models";
import { TranscriptView } from "../lib/transcript";
import type { RpcError } from "../lib/rpc-client";

/**
 * The choices a new chat is configured with, held in the store so the screen can
 * be re-rendered without losing them (and so a reload cannot silently retarget
 * the chat to a different project).
 *
 * This is T3's `ComposerDraft` in miniature: a project, a base ref, and whether
 * to run in an isolated worktree, plus the harness/model the first turn uses.
 */
export interface NewChatDraft {
  /** The project (space) the chat is homed on. `undefined` = device home dir. */
  spaceId?: string;
  /** The base ref a worktree is cut from, or the branch to check out. */
  baseRef?: string;
  /** Run in a fresh isolated worktree instead of the project folder. */
  worktree: boolean;
  harness?: string;
  model?: string | null;
  reasoning?: string | null;
  sandbox?: SandboxLevel;
}

export const emptyNewChatDraft = (): NewChatDraft => ({
  spaceId: undefined,
  baseRef: undefined,
  worktree: false,
  harness: undefined,
  model: null,
  reasoning: null,
  sandbox: undefined
});

/**
 * The change requests of one repository, as the browse surface shows them.
 *
 * Addressed by checkout: `repository` and `host` come back from the engine's own
 * read of the remote, so the panel can title itself without the browser having
 * to derive anything.
 */
export interface ChangeRequestList {
  repository: string;
  host: string;
  items: ChangeRequestSummary[];
  /** Which list is showing. Kept so the toggle can re-issue the same query. */
  state: "open" | "closed" | "all";
}

export interface ChangeRequestSummary {
  provider: string;
  number: number;
  title: string;
  url: string;
  state: "open" | "closed" | "merged" | string;
  isDraft: boolean;
  baseRef: string;
  headRef: string;
  mergedAt?: string | null;
}

export interface ComposerDraft {
  text: string;
  /** `run` starts a turn; `queue` holds the text in the chat's durable queue. */
  mode: "run" | "queue";
  /**
   * The client-minted run id, held for as long as `text` is the text it was
   * minted for. This is the engine's dedup key: reusing it for the same content
   * is what makes a retry idempotent instead of a second turn.
   */
  messageId?: string;
  harness?: string;
  model?: string | null;
  reasoning?: string | null;
  sandbox?: string;
}

export const emptyDraft = (): ComposerDraft => ({
  text: "",
  mode: "run",
  harness: undefined,
  model: null,
  reasoning: null,
  sandbox: "workspaceWrite"
});

export interface AppSnapshot {
  auth: "anonymous" | "authenticated";
  link: SessionState["link"];
  linkDetail?: string;
  problem?: string;
  devices: RegistryDevice[];
  deviceId?: string;
  engineInfo?: EngineInfo;
  chats: Chat[];
  spaces: Space[];
  /** Pinned conversation ids, from the synced `sidebarPins` registry row. Synced
   * rather than local, so a pin set on the desktop shows up here. */
  pinnedChatIds: string[];
  sessions: Record<string, Session>;
  openChatId?: string;
  transcriptRevision: number;
  historyPending: boolean;
  queue: QueuedMessage[];
  draft: ComposerDraft;
  /** The open new-chat screen's choices; `undefined` = no screen open. */
  newChat?: NewChatDraft;
  /** Base refs of the new-chat screen's chosen project. Held in the snapshot
   * rather than returned only, so the picker can render them. */
  refs?: string[];
  /** The open chat's repository change requests, for browsing. */
  changeRequests?: ChangeRequestList;
  harnesses: HarnessDescriptor[];
  models: Model[];
  /** Set while a send is in flight, so the composer can disable itself. */
  sending: boolean;
  /** Human-readable result of the last action. */
  notice?: string;
  error?: string;
  /** Who is signed in, for the account menu. */
  userLabel?: string;
  userInitial?: string;
}

export interface AppStoreOptions {
  session: ZeronSession;
  auth: AuthClient;
  /** Injectable so the message id is deterministic in tests. */
  newId?: () => string;
  now?: () => number;
  /** Device-id memory. Defaults to `localStorage`; injectable for tests. */
  storage?: Pick<Storage, "getItem" | "setItem">;
  /** Device-list source. Defaults to the BFF's `/api/devices`; injectable so a
   * test does not have to stand up a server to exercise the picker. */
  fetchDevices?: () => Promise<RegistryDevice[]>;
}

/**
 * The default device-list source: the BFF route, which reads the account
 * registry server-side.
 *
 * It has to be the BFF rather than the edge directly — the registry's HTTP read
 * sends no CORS headers, so a page on another origin cannot call it — and it is
 * server-side for a second reason too: the access token never enters the page.
 */
export const fetchDevicesFromBff = async (accessToken?: string): Promise<RegistryDevice[]> => {
  const headers: Record<string, string> = {};
  // Presenting the page's own token is what keeps the BFF from refreshing by
  // itself. WorkOS rotates refresh tokens on every use, so a second refresh path
  // races the client's and the loser is treated as revoked — which signed the
  // user out mid-session.
  if (accessToken !== undefined && accessToken.length > 0) {
    headers.authorization = `Bearer ${accessToken}`;
  }
  const response = await fetch("/api/devices", {
    method: "GET",
    credentials: "same-origin",
    headers
  });
  if (!response.ok) throw new Error(`device list failed (${response.status})`);
  const body = (await response.json()) as { devices?: unknown };
  return Array.isArray(body.devices) ? (body.devices as RegistryDevice[]) : [];
};

export const defaultNewId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export class AppStore {
  private snapshot: AppSnapshot;
  private readonly session: ZeronSession;
  private readonly auth: AuthClient;
  private readonly newId: () => string;
  private readonly now: () => number;
  private readonly fetchDevicesImpl: (accessToken?: string) => Promise<RegistryDevice[]>;
  private readonly listeners = new Set<(snapshot: AppSnapshot) => void>();
  private readonly transcript = new TranscriptView();
  private readonly storage: Pick<Storage, "getItem" | "setItem"> | undefined;
  private openChatGeneration = 0;

  constructor(options: AppStoreOptions) {
    this.session = options.session;
    this.auth = options.auth;
    this.newId = options.newId ?? defaultNewId;
    this.now = options.now ?? (() => Date.now());
    this.storage = options.storage ?? safeLocalStorage();
    this.fetchDevicesImpl = options.fetchDevices ?? fetchDevicesFromBff;
    // Mirror the session's link state into the snapshot. Without this the UI
    // renders `idle` forever while calls work fine — the link knew, the store
    // did not. A successful reconnect also clears the errors the failed attempt
    // produced: leaving "EngineInfo: timed out" on screen after the link is
    // healthy again is worse than showing nothing.
    this.session.subscribeState((state) => {
      const recovered = state.link === "online" && this.snapshot.link !== "online";
      this.patch({
        link: state.link,
        linkDetail: state.detail,
        problem: state.problem,
        ...(recovered ? { error: undefined } : {})
      });
    });
    this.snapshot = {
      auth: "anonymous",
      link: "idle",
      devices: [],
      chats: [],
      spaces: [],
      pinnedChatIds: [],
      sessions: {},
      transcriptRevision: 0,
      historyPending: false,
      queue: [],
      draft: emptyDraft(),
      newChat: undefined,
      harnesses: [],
      models: [],
      sending: false
    };
  }

  get state(): AppSnapshot {
    return this.snapshot;
  }

  get transcriptView(): TranscriptView {
    return this.transcript;
  }

  subscribe(listener: (snapshot: AppSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => void this.listeners.delete(listener);
  }

  /** Boot: restore a session, then connect to a device and register watches. */
  async start(): Promise<void> {
    const restored = await this.auth.restore();
    if (restored.status !== "authenticated") {
      this.patch({ auth: "anonymous" });
      return;
    }
    const user = this.auth.user;
    this.patch({
      auth: "authenticated",
      // The account menu shows who is signed in. A name is nicer than an email
      // but is not always present, so both are kept and the view picks.
      userLabel: user?.email ?? user?.id,
      userInitial: (user?.email ?? user?.id ?? "?").slice(0, 1).toUpperCase()
    });
    // Learn the user's devices from the server first. Without this the picker
    // can only offer ids this browser happened to remember, which on a fresh
    // device (a new iPad) is nothing at all.
    await this.refreshDevices();
    await this.connectFirstDevice();
  }

  /**
   * Ask the BFF for the account's devices (it reads the registry server-side).
   *
   * Failure is not fatal: a browser that already knows a device id can still
   * connect, so this reports nothing and leaves the remembered list in place.
   */
  async refreshDevices(): Promise<void> {
    try {
      const devices = await this.fetchDevices();
      this.patch({ devices });
      for (const device of devices) this.rememberDevice(device.id);
    } catch {
      // Offline, or the registry is unhappy. The remembered ids still work.
    }
  }

  /** Fetch and order the device list. */
  private async fetchDevices(): Promise<RegistryDevice[]> {
    // The token is fetched through the auth client's *serialized* refresh, so
    // this never races the relay's own 401 recovery.
    const token = await this.auth.ensureAccessToken();
    const devices = await this.fetchDevicesImpl(token);
    // Sort by most recently seen so the picker's first entry is the machine the
    // user is most likely to want; a device that never stamped a time sorts last.
    return [...devices].sort((a, b) => (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0));
  }

  /** Connect to a device by id, refreshing the list first so the picker shows
   * the machine even if this browser has never seen it. */
  async addDeviceById(deviceId: string): Promise<void> {
    await this.refreshDevices();
    await this.connectDevice(deviceId);
  }

  /**
   * Liveness probe for a link that *looks* connected.
   *
   * Safari suspends timers while a tab is backgrounded, so a socket can appear
   * open long after the edge reaped it. On resume there is no way to tell from
   * `readyState` alone, so send an echo and let the link's deadline decide. The
   * host's silence lease (25s) is longer than the echo deadline (20s), so a dead
   * relay is always caught by this rather than by the host giving up.
   *
   * When the link is *not* online this escalates to a forced redial, because an
   * echo on a closed socket does nothing and a tab resumed after hours of
   * suspension is the single most likely moment to find one. The redial goes
   * through the session rather than `selectDevice`, so the snapshot (chat list,
   * open chat, queue) survives — a tab switch must not blank the screen.
   */
  probe(): void {
    const link = this.snapshot.link;
    if (link === "online" || link === "host_offline") {
      this.session.probe();
      return;
    }
    if (this.snapshot.deviceId === undefined) return;
    void this.session.reconnect().catch((error: unknown) => {
      this.patch({ problem: describe(error) });
    });
  }

  /**
   * The explicit Retry button. Like `probe`, it redials in place so the chat
   * list is not thrown away — but it also clears the stale error text, since the
   * user has acknowledged it by pressing the button.
   */
  async retry(): Promise<void> {
    const deviceId = this.snapshot.deviceId;
    if (deviceId === undefined) {
      await this.connectFirstDevice();
      return;
    }
    this.patch({ error: undefined, problem: undefined });
    try {
      await this.session.reconnect();
      const info = await this.session.engineInfo();
      this.patch({ engineInfo: info, error: undefined, problem: undefined });
    } catch (error) {
      // Still down. Leave the reason on screen; the link keeps backing off.
      this.patch({ problem: `${describe(error)} — the device may be offline` });
    }
  }

  /** Add a device id by hand (there is no edge endpoint to enumerate them). */
  async connectDevice(deviceId: string): Promise<void> {
    const trimmed = deviceId.trim();
    if (trimmed.length === 0) return;
    await this.selectDevice(trimmed);
  }

  /**
   * The browser cannot enumerate the user's devices without a device to ask
   * (there is no `/devices` edge endpoint), so it asks each known device id from
   * `localStorage` — written every time a device connects successfully — plus
   * any id the user pasted. The first one that answers `EngineInfo` becomes the
   * connection, and its own `WatchDevices` then supplies the real list.
   */
  async connectFirstDevice(): Promise<void> {
    // Prefer a device the server just told us about, in last-seen order: on a
    // fresh browser that is the only source, and on a returning one it keeps the
    // choice current (a device that was removed should not be retried first).
    const discovered = this.snapshot.devices.map((device) => device.id);
    const remembered = this.knownDeviceIds();
    const candidates = [...new Set([...discovered, ...remembered])];
    if (candidates.length === 0) {
      this.patch({ problem: "No devices are registered to this account yet." });
      return;
    }
    await this.selectDevice(candidates[0] as string);
  }

  knownDeviceIds(): string[] {
    try {
      const raw = this.storage?.getItem("zeron.devices");
      const parsed = raw === null || raw === undefined ? [] : (JSON.parse(raw) as unknown);
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
    } catch {
      return [];
    }
  }

  rememberDevice(deviceId: string): void {
    const ids = [deviceId, ...this.knownDeviceIds().filter((id) => id !== deviceId)].slice(0, 20);
    try {
      this.storage?.setItem("zeron.devices", JSON.stringify(ids));
    } catch {
      /* private mode: the picker just starts empty next time */
    }
  }

  /**
   * Switch devices: tear down the old link, reconnect, re-register watches.
   *
   * This is the *device switch* path, so it deliberately throws the view away
   * (chats, queue, open chat) — those belonged to the old device. Recovering the
   * current device is `retry()`/`probe()`, which call `session.reconnect()`
   * instead and keep the snapshot.
   */
  async selectDevice(deviceId: string): Promise<void> {
    // Drop the previous device's watches. Their keys embed the device id
    // (`chats:<id>`), so leaving them would keep reissuing subscriptions to a
    // device the user has navigated away from on every reconnect.
    this.session.clearWatches();
    this.transcript.reset();
    this.openChatGeneration += 1;
    this.patch({
      deviceId,
      openChatId: undefined,
      queue: [],
      chats: [],
      sessions: {},
      transcriptRevision: this.transcript.revision,
      problem: undefined,
      error: undefined
    });
    // Register the watches *before* waiting for the socket. `session.watch`
    // remembers the registration and reissues it on every `onReady`, so a first
    // dial that fails but recovers on backoff still ends up subscribed.
    // Registering after the await would leave a recovered link permanently
    // silent — connected, with an empty chat list and no way to notice.
    this.registerDeviceWatches(deviceId);
    try {
      await this.session.connect(deviceId);
    } catch (error) {
      // The link may still be retrying in the background; the watches are
      // already registered for whenever it lands.
      this.patch({ error: `could not reach ${deviceId}: ${describe(error)}` });
      return;
    }
    try {
      const info = await this.session.engineInfo();
      this.patch({ engineInfo: info, problem: undefined, error: undefined });
      this.rememberDevice(deviceId);
      void this.loadHarnesses();
    } catch (error) {
      // The relay is up but no host is behind it: the device is asleep.
      this.patch({ problem: `${describe(error)} — the device may be offline` });
    }
  }

  private registerDeviceWatches(deviceId: string): void {
    this.session.watch(`chats:${deviceId}`, "WatchChats", {}, {
      onItem: (item) => this.patch({ chats: sortChats(asArray<Chat>(item)) })
    });
    this.session.watch(`sessions:${deviceId}`, "WatchSessions", {}, {
      onItem: (item) => this.patch({ sessions: indexBy(asArray<Session>(item), (s) => s.chatId) })
    });
    this.session.watch(`devices:${deviceId}`, "WatchDevices", {}, {
      // Normalized, not assigned raw: the registry read and this watch feed the
      // same list, and the picker must not have to know which one answered.
      onItem: (item) => this.patch({ devices: asArray<Device>(item).map(asRegistryDevice) })
    });
    // Projects. The composer's new-chat screen picks one, and `createChat`
    // needs its id to home the chat on the right folder.
    this.session.watch(`spaces:${deviceId}`, "WatchSpaces", {}, {
      onItem: (item) => this.patch({ spaces: sortSpaces(asArray<Space>(item)) })
    });
    // Sidebar pins, so a chat pinned on the desktop reads as pinned here.
    this.session.watch(`sidebar:${deviceId}`, "WatchSidebarPreferences", {}, {
      onItem: (item) => {
        const payload = item as { pinnedSessionIds?: unknown };
        this.patch({
          pinnedChatIds: Array.isArray(payload?.pinnedSessionIds)
            ? payload.pinnedSessionIds.filter((id): id is string => typeof id === "string")
            : []
        });
      }
    });
    this.session.watch(`connectivity:${deviceId}`, "WatchConnectivity", {}, { onItem: () => {} });
  }

  async loadHarnesses(): Promise<void> {
    try {
      const harnesses = await this.session.listHarnesses();
      this.patch({ harnesses: asArray<HarnessDescriptor>(harnesses) });
    } catch {
      // Optional: the composer falls back to the chat's own config.
    }
  }

  async loadModels(harness: string): Promise<void> {
    try {
      const models = await this.session.listModels(harness);
      this.patch({ models: asArray<Model>(models) });
    } catch {
      this.patch({ models: [] });
    }
  }

  /** Open a chat: watch its transcript and queue, and mark it seen. */
  async openChat(chatId: string): Promise<void> {
    // Release the previous chat's watches first. Leaving them registered would
    // keep a second transcript streaming into a view nobody renders, and the
    // engine would keep producing delta frames for a hidden chat forever.
    const previous = this.snapshot.openChatId;
    if (previous !== undefined && previous !== chatId) {
      this.session.unwatch(`doc:${previous}`);
      this.session.unwatch(`queue:${previous}`);
    }
    const generation = ++this.openChatGeneration;
    this.transcript.reset();
    this.patch({
      openChatId: chatId,
      queue: [],
      transcriptRevision: this.transcript.revision,
      error: undefined,
      notice: undefined
    });
    const chat = this.snapshot.chats.find((candidate) => candidate.id === chatId);
    // Transcript and queue live on the chat's host device; if we are connected
    // to a different device the engine forwards them.
    const targetDeviceId =
      chat !== undefined && chat.deviceId !== this.snapshot.deviceId ? chat.deviceId : undefined;
    this.session.watch(
      `doc:${chatId}`,
      "WatchDocMessages",
      { chatId, openingTail: true },
      {
        targetDeviceId,
        onItem: (item) => {
          if (generation !== this.openChatGeneration) return;
          const applied = this.transcript.apply(item);
          if (!applied) {
            // Desync: the only repair is a fresh reset.
            this.resubscribeDoc(chatId, targetDeviceId, generation);
            return;
          }
          this.patch({
            transcriptRevision: this.transcript.revision,
            historyPending: this.transcript.state.historyPending
          });
        },
        onError: (error: RpcError) => {
          if (generation !== this.openChatGeneration) return;
          this.patch({ error: `transcript: ${error.message}` });
        }
      }
    );
    this.session.watch(`queue:${chatId}`, "WatchQueue", { chatId }, {
      targetDeviceId,
      onItem: (item) => {
        if (generation !== this.openChatGeneration) return;
        const payload = item as { items?: QueuedMessage[] };
        this.patch({ queue: Array.isArray(payload?.items) ? payload.items : [] });
      }
    });
    if (chat !== undefined && chat.lastSeenAt === null && chat.lastMessageAt !== null) {
      void this.session.markChatSeen(chatId).catch(() => {});
    }
  }

  private resubscribeDoc(chatId: string, targetDeviceId: string | undefined, generation: number): void {
    this.transcript.reset();
    this.patch({ transcriptRevision: this.transcript.revision, notice: "resyncing transcript…" });
    this.session.watch(
      `doc:${chatId}`,
      "WatchDocMessages",
      { chatId },
      {
        targetDeviceId,
        onItem: (item) => {
          if (generation !== this.openChatGeneration) return;
          if (!this.transcript.apply(item)) return;
          this.patch({ transcriptRevision: this.transcript.revision, historyPending: false });
        }
      }
    );
  }

  closeChat(): void {
    const openChatId = this.snapshot.openChatId;
    if (openChatId !== undefined) {
      this.session.unwatch(`doc:${openChatId}`);
      this.session.unwatch(`queue:${openChatId}`);
    }
    this.openChatGeneration += 1;
    this.transcript.reset();
    this.patch({
      openChatId: undefined,
      queue: [],
      transcriptRevision: this.transcript.revision,
      historyPending: false
    });
  }

  setDraft(patch: Partial<ComposerDraft>): void {
    const next = { ...this.snapshot.draft, ...patch };
    // A run id belongs to the exact text it was minted for. Once the text
    // changes it must be dropped, or a later send would reuse the id for
    // different content — which the engine would treat as a duplicate.
    if (patch.text !== undefined && patch.text !== this.snapshot.draft.text) {
      next.messageId = undefined;
    }
    this.patch({ draft: next });
  }

  /**
   * Send the composer's text. `run` mints a client message id (the engine's
   * dedup key); the id is kept on the draft so a retry of the same text reuses
   * it and cannot double-run.
   */
  async send(): Promise<void> {
    const { openChatId, draft } = this.snapshot;
    const text = draft.text.trim();
    if (openChatId === undefined || text.length === 0) return;
    const chat = this.snapshot.chats.find((candidate) => candidate.id === openChatId);
    if (chat === undefined) return;
    const targetDeviceId = chat.deviceId !== this.snapshot.deviceId ? chat.deviceId : undefined;
    const messageId = draft.messageId ?? this.newId();
    // Clear the composer and show the in-flight state *before* awaiting.
    //
    // The round trip is a websocket call to a device that may be busy, so a send
    // that only reacted on the reply looked like a dead button and invited a
    // second tap. The id is kept alongside the text it belongs to so that a
    // retry after a failure is idempotent rather than a duplicate turn.
    this.patch({
      sending: true,
      error: undefined,
      notice: undefined,
      draft: { ...draft, text: "", messageId }
    });
    try {
      if (draft.mode === "queue") {
        await this.session.queueMessage({ chatId: openChatId, text, targetDeviceId });
        this.patch({ notice: "held in the queue" });
      } else {
        await this.session.runPrompt({
          chatId: openChatId,
          prompt: text,
          messageId,
          cwd: chat.cwd ?? "",
          harness: draft.harness ?? chat.config?.harness,
          model: draft.model ?? chat.config?.model ?? null,
          reasoning: draft.reasoning ?? chat.config?.reasoning ?? null,
          sandbox: draft.sandbox ?? chat.config?.sandbox ?? "workspaceWrite",
          targetDeviceId
        });
        this.patch({ notice: "sent" });
      }
      // Accepted: the id has served its purpose.
      this.patch({ draft: { ...this.snapshot.draft, messageId: undefined } });
    } catch (error) {
      // Put the text back so the prompt is not lost, and keep the id so that
      // sending again is the same run rather than a new one.
      this.patch({
        error: describe(error),
        draft: { ...this.snapshot.draft, text, messageId }
      });
    } finally {
      this.patch({ sending: false });
    }
  }

  async interrupt(): Promise<void> {
    const { openChatId } = this.snapshot;
    if (openChatId === undefined) return;
    const chat = this.snapshot.chats.find((candidate) => candidate.id === openChatId);
    const targetDeviceId =
      chat !== undefined && chat.deviceId !== this.snapshot.deviceId ? chat.deviceId : undefined;
    try {
      await this.session.interrupt(openChatId, targetDeviceId);
      this.patch({ notice: "interrupt sent" });
    } catch (error) {
      this.patch({ error: describe(error) });
    }
  }

  async respondInput(
    requestId: string,
    answers: { questionId: string; labels: string[] }[]
  ): Promise<void> {
    const { openChatId } = this.snapshot;
    if (openChatId === undefined) return;
    const chat = this.snapshot.chats.find((candidate) => candidate.id === openChatId);
    const targetDeviceId =
      chat !== undefined && chat.deviceId !== this.snapshot.deviceId ? chat.deviceId : undefined;
    try {
      await this.session.respondInput({ chatId: openChatId, requestId, answers, targetDeviceId });
      this.patch({ notice: "answer sent" });
    } catch (error) {
      this.patch({ error: describe(error) });
    }
  }

  /**
   * Start a new chat on the connected device and open it.
   *
   * The engine mints the row, but `WatchChats` only reports rows it knows about,
   * so the chat is inserted locally too: waiting for the watch to echo back
   * would leave the user staring at the old transcript after tapping "New chat",
   * which is exactly the "nothing happened" complaint the send button had.
   */
  /**
   * Open the new-chat screen.
   *
   * A chat is not minted here: the user still has to choose a project and a
   * model, and minting first would leave an empty row behind every time the
   * screen was opened and abandoned. `createChatFromDraft` does the minting.
   */
  openNewChat(): void {
    // Seed the pickers from the last chat, the way the desktop app does: the
    // defaults a user wants are almost always the ones they just used.
    const last = this.snapshot.chats.find((chat) => chat.config !== undefined)?.config;
    this.patch({
      newChat: {
        ...emptyNewChatDraft(),
        harness: last?.harness,
        model: last?.model ?? null,
        reasoning: last?.reasoning ?? null,
        sandbox: last?.sandbox
      },
      error: undefined,
      notice: undefined
    });
    // The pickers need the catalog, and it may not have loaded yet (a fresh
    // connect only asks for harnesses; models are per-harness). Loading here
    // means the screen opens populated instead of filling in a beat later.
    void this.ensureHarnesses().then((harnesses) => {
      const harness = this.snapshot.newChat?.harness ?? harnesses[0]?.id;
      if (harness !== undefined) void this.loadModels(harness);
    });
  }

  closeNewChat(): void {
    if (this.snapshot.newChat === undefined) return;
    this.patch({ newChat: undefined });
  }

  setNewChat(patch: Partial<NewChatDraft>): void {
    const current = this.snapshot.newChat;
    if (current === undefined) return;
    const next = { ...current, ...patch };
    // Changing the project invalidates a ref picked from the old one.
    if (patch.spaceId !== undefined && patch.spaceId !== current.spaceId) {
      next.baseRef = undefined;
    }
    this.patch({ newChat: next });
  }

  /** The harness a new chat would use, once one is chosen. */
  private async ensureHarnesses(): Promise<HarnessDescriptor[]> {
    if (this.snapshot.harnesses.length === 0) await this.loadHarnesses();
    return this.snapshot.harnesses;
  }

  async loadModelsForNewChat(harness: string): Promise<void> {
    await this.loadModels(harness);
  }

  /**
   * Base refs of a project, for the new-chat screen's "Start from" picker.
   *
   * Failure is not fatal — the picker falls back to the default branch — so this
   * reports nothing and returns an empty list.
   */
  async loadRefs(space: Space): Promise<string[]> {
    const targetDeviceId = space.deviceId !== this.snapshot.deviceId ? space.deviceId : undefined;
    try {
      const raw = await this.session.listRefs(space.path, targetDeviceId);
      const list = asArray<{ name?: unknown; refName?: unknown; branch?: unknown }>(raw);
      const refs = list
        .map((ref) => {
          const name = ref.name ?? ref.refName ?? ref.branch;
          return typeof name === "string" ? name : undefined;
        })
        .filter((name): name is string => name !== undefined && name.length > 0);
      // Published so the picker can show them. A late reply for a project the
      // user has since navigated away from must not populate the new one's list.
      if (this.snapshot.newChat?.spaceId === space.id) this.patch({ refs });
      return refs;
    } catch {
      return [];
    }
  }

  /**
   * Mint the chat the new-chat screen is configured with, then open it.
   *
   * The row is inserted locally as well as on the engine: `WatchChats` only
   * reports what the host knows about, and waiting for that echo leaves the user
   * staring at the old screen after tapping Create.
   */
  async createChatFromDraft(): Promise<string | undefined> {
    const deviceId = this.snapshot.deviceId;
    const draft = this.snapshot.newChat;
    if (draft === undefined) return undefined;
    if (deviceId === undefined) {
      this.patch({ problem: "Connect to a device before starting a chat." });
      return undefined;
    }
    const space =
      draft.spaceId === undefined
        ? undefined
        : this.snapshot.spaces.find((candidate) => candidate.id === draft.spaceId);
    if (draft.spaceId !== undefined && space === undefined) {
      this.patch({ error: "That project is no longer available." });
      return undefined;
    }
    const harnesses = await this.ensureHarnesses();
    const harness = draft.harness ?? harnesses[0]?.id;
    if (harness === undefined) {
      this.patch({ error: "No agent harness is installed on that device." });
      return undefined;
    }
    const config: ChatConfig = {
      harness,
      model: draft.model ?? null,
      reasoning: draft.reasoning ?? null,
      modelOptions: {},
      sandbox: draft.sandbox ?? "workspaceWrite"
    };
    const chatId = this.newId();
    const targetDeviceId = space !== undefined && space.deviceId !== deviceId ? space.deviceId : undefined;
    try {
      await this.session.createChat({
        chatId,
        spaceId: space?.id,
        deviceId: space === undefined ? deviceId : undefined,
        config,
        branch: draft.baseRef,
        targetDeviceId
      });
      const chat: Chat = {
        id: chatId,
        deviceId: space?.deviceId ?? deviceId,
        title: null,
        archived: false,
        cwd: space?.path ?? null,
        branch: draft.baseRef ?? null,
        checkoutId: null,
        config,
        lastMessagePreview: null,
        lastMessageAt: null,
        lastSeenAt: null,
        createdAt: new Date(this.now()).toISOString(),
        spaceId: space?.id ?? null
      };
      this.patch({
        chats: [chat, ...this.snapshot.chats],
        newChat: undefined,
        notice: undefined,
        error: undefined
      });
      await this.openChat(chatId);
      return chatId;
    } catch (error) {
      this.patch({ error: describe(error) });
      return undefined;
    }
  }

  /**
   * Load the open chat's repository change requests.
   *
   * Failure is reported but not fatal: a checkout with no recognised remote
   * cannot list anything, and that is an ordinary state rather than an error the
   * user must act on.
   */
  async loadChangeRequests(state: "open" | "closed" | "all" = "open"): Promise<void> {
    const { openChatId } = this.snapshot;
    if (openChatId === undefined) return;
    const chat = this.snapshot.chats.find((candidate) => candidate.id === openChatId);
    if (chat === undefined) return;
    const cwd = chat.cwd ?? "";
    if (cwd.length === 0) {
      this.patch({ changeRequests: undefined });
      return;
    }
    const targetDeviceId = chat.deviceId !== this.snapshot.deviceId ? chat.deviceId : undefined;
    try {
      const raw = (await this.session.listChangeRequests(cwd, state, targetDeviceId)) as {
        changeRequests?: unknown;
        repository?: unknown;
        host?: unknown;
      };
      // A late reply for a chat the user has navigated away from must not
      // populate the panel for the chat they are now looking at.
      if (this.snapshot.openChatId !== openChatId) return;
      this.patch({
        changeRequests: {
          repository: typeof raw.repository === "string" ? raw.repository : "",
          host: typeof raw.host === "string" ? raw.host : "",
          items: asArray<ChangeRequestSummary>(raw.changeRequests),
          state
        }
      });
    } catch (error) {
      if (this.snapshot.openChatId !== openChatId) return;
      // No remote, no provider CLI, or not a repository: not worth an error
      // banner over the transcript.
      this.patch({ changeRequests: undefined });
    }
  }

  async removeQueued(id: string): Promise<void> {
    const { openChatId } = this.snapshot;
    if (openChatId === undefined) return;
    try {
      await this.session.removeQueued(openChatId, id);
    } catch (error) {
      this.patch({ error: describe(error) });
    }
  }

  async sendQueuedNow(id: string): Promise<void> {
    const { openChatId } = this.snapshot;
    if (openChatId === undefined) return;
    try {
      await this.session.sendQueuedNow(openChatId, id);
    } catch (error) {
      this.patch({ error: describe(error) });
    }
  }

  async signOut(): Promise<void> {
    this.session.disconnect();
    await this.auth.signOut();
    this.patch({
      auth: "anonymous",
      chats: [],
      devices: [],
      sessions: {},
      openChatId: undefined,
      queue: [],
      engineInfo: undefined
    });
  }

  /** Derived: the open chat's display indicator. */
  indicatorFor(chatId: string): ReturnType<typeof chatIndicator> {
    const chat = this.snapshot.chats.find((candidate) => candidate.id === chatId);
    if (chat === undefined) return "idle";
    return chatIndicator(chat, this.snapshot.sessions[chatId], this.now());
  }

  statusFor(chatId: string): SessionStatus | undefined {
    return this.snapshot.sessions[chatId]?.status;
  }

  /** Chats whose session says the agent is waiting on the user. */
  awaitingInput(): Chat[] {
    return this.snapshot.chats.filter((chat) => this.snapshot.sessions[chat.id]?.status === "awaitingInput");
  }

  /** Chats with a live (non-stale) session, most recently active first. */
  activeChats(): Chat[] {
    const now = this.now();
    return this.snapshot.chats.filter((chat) => {
      const session = this.snapshot.sessions[chat.id];
      if (session === undefined) return false;
      const updated = toMillis(session.updatedAt);
      return updated !== undefined && now - updated <= 45_000;
    });
  }

  private patch(patch: Partial<AppSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener(this.snapshot);
  }
}

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/** `localStorage` throws in Safari private mode and does not exist in Node. */
const safeLocalStorage = (): Pick<Storage, "getItem" | "setItem"> | undefined => {
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
};

const indexBy = <T>(items: T[], key: (item: T) => string): Record<string, T> => {
  const out: Record<string, T> = {};
  for (const item of items) out[key(item)] = item;
  return out;
};

const describe = (error: unknown): string => {
  if (error !== null && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
};
